
import { useMemo, useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/auth";
import { Button, Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage, Input } from "@/components/ui";
import { useSubscription } from "@/hooks/subscription";
import VerifyWhatsAppButton from "@/components/VerifyWhatsAppButton";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useTranslation } from "react-i18next";
import { sanitizeRedirectPath } from '@/utils/auth/sanitizeRedirect';

type ProfileFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  whatsappNumber: string;
};

interface ProfileDataShape {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  whatsapp_number?: string | null;
  whatsapp_verified?: boolean;
}

export const ProfileForm = ({
  profileData
}: {
  profileData: ProfileDataShape;
}) => {
  const {
    updateProfile,
    user,
  } = useAuth();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    data: subscriptionStatus
  } = useSubscription();
  const userTier = subscriptionStatus?.tier || 'free_tier';
  const isCompletionMode = searchParams.get('complete') === 'true';
  const isCheckoutEmailCaptureMode = searchParams.get('requireEmail') === 'true';
  const completionRedirectPath = sanitizeRedirectPath(searchParams.get('redirect'));
  const { t } = useTranslation('common');
  const fallbackPhoneNumber = profileData.whatsapp_number || user?.phone || "";
  const isPhoneAuthUser = user?.app_metadata?.provider === 'phone' || (!user?.email && Boolean(user?.phone));
  const requiresAuthSetup = isPhoneAuthUser && !user?.email;
  const emailRequired = isPhoneAuthUser || isCheckoutEmailCaptureMode;
  const canEditEmail = isPhoneAuthUser;

  const profileSchema = useMemo(() => z.object({
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    email: z.union([z.literal(""), z.string().trim().email("Invalid email address")]),
    whatsappNumber: z.string().min(1, "WhatsApp number is required"),
  }).superRefine((value, ctx) => {
    if (emailRequired && value.email.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['email'],
        message: 'Email is required for Stripe billing receipts and account notifications.',
      });
    }
  }), [emailRequired]);

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: profileData.first_name || "",
      lastName: profileData.last_name || "",
      email: profileData.email || "",
      whatsappNumber: fallbackPhoneNumber,
    }
  });

  const watchWhatsappNumber = form.watch("whatsappNumber");

  const onSubmit = async (data: ProfileFormValues) => {
    setIsSubmitting(true);
    try {
      const normalizedEmail = data.email.trim();

      await updateProfile({
        first_name: data.firstName,
        last_name: data.lastName,
        ...(canEditEmail ? { email: normalizedEmail || null } : {}),
        whatsapp_number: data.whatsappNumber,
      });

      if (requiresAuthSetup && normalizedEmail) {
        const nextParams = new URLSearchParams({
          tab: 'security',
          authSetup: 'true',
        });

        if (completionRedirectPath !== '/') {
          nextParams.set('redirect', completionRedirectPath);
        }

        window.location.href = `/profile?${nextParams.toString()}`;
        return;
      }

      if (isCompletionMode || isCheckoutEmailCaptureMode) {
        window.location.href = completionRedirectPath;
      }
    } catch (error) {
      console.error("Failed to update profile:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return <section className="overflow-hidden rounded-md border border-brand-cloud bg-white shadow-sm">
      <div className="border-b border-brand-cloud p-6 md:p-8">
        <h2 className="public-section-title">
          {isCompletionMode ? t("profile.form.completeTitle") : t("profile.form.title")}
        </h2>
        <p className="mt-2 text-sm leading-6 text-brand-slate">
          {isCompletionMode ? t("profile.form.completeDescription") : t("profile.form.description")}
        </p>
      </div>
      <div className="p-6 md:p-8">
        {isCompletionMode && (
          <Alert className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t("profile.completeAlert")}
            </AlertDescription>
          </Alert>
        )}

        {requiresAuthSetup && (
          <Alert className="mb-6 border-amber-200 bg-amber-50 text-amber-900">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Add an email address to create an email sign-in and enable password reset links for this account.
            </AlertDescription>
          </Alert>
        )}

        {isCheckoutEmailCaptureMode && (
          <Alert className="mb-6 border-amber-200 bg-amber-50 text-amber-900">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Add an email address before starting a paid subscription. Stripe receipts and platform email notifications are sent there.
            </AlertDescription>
          </Alert>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <FormField control={form.control} name="firstName" render={({
              field
            }) => <FormItem className="space-y-2">
                    <FormLabel>{t("profile.form.firstName")} {t("profile.form.required")}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={form.control} name="lastName" render={({
              field
            }) => <FormItem className="space-y-2">
                    <FormLabel>{t("profile.form.lastName")} {t("profile.form.required")}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <FormField control={form.control} name="email" render={({
              field
            }) => <FormItem className="space-y-2">
                    <FormLabel>{t("profile.form.email")}{emailRequired ? ` ${t("profile.form.required")}` : ''}</FormLabel>
                    <FormControl>
                      <Input {...field} disabled={!canEditEmail} readOnly={!canEditEmail} />
                    </FormControl>
                    <FormDescription>
                      {canEditEmail
                        ? 'Required for subscriptions, receipts, and email notifications.'
                        : 'Your sign-in email is managed by your login method.'}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={form.control} name="whatsappNumber" render={({
              field
            }) => <FormItem className="space-y-2">
                    <div className="flex items-center gap-2">
                      <FormLabel>{t("profile.form.whatsappNumber")} {t("profile.form.required")}</FormLabel>
                      {profileData.whatsapp_verified && <Badge variant="outline" className="border-teal bg-teal-soft text-teal">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          {t("profile.form.whatsappVerified")}
                        </Badge>}
                    </div>
                    <div className="flex gap-2 items-start">
                      <div className="flex-1">
                        <FormControl>
                          <Input {...field} placeholder={t("profile.form.whatsappPlaceholder")} />
                        </FormControl>
                      </div>
                      {watchWhatsappNumber && (userTier === 'whatsapp_tier' || userTier === 'premium_tier') && !profileData.whatsapp_verified && (
                        <VerifyWhatsAppButton 
                          phoneNumber={watchWhatsappNumber} 
                          isVerified={profileData.whatsapp_verified} 
                          onVerificationSuccess={() => {
                            // Refresh page after successful verification
                            window.location.reload();
                          }} 
                        />
                      )}
                    </div>
                    <FormDescription>{t("profile.form.whatsappDescription")}</FormDescription>
                    <FormMessage />
                  </FormItem>} />

            </div>

            <Button type="submit" disabled={isSubmitting} className="w-full">
              {isSubmitting ? t("profile.form.saving") : isCompletionMode ? t("profile.form.complete") : t("profile.form.save")}
            </Button>
          </form>
        </Form>
      </div>
    </section>;
};
